import type {} from '@mui/lab/themeAugmentation'
import { createTheme, ThemeOptions, ThemeProvider } from '@mui/material/styles'

import { ThemeInterface } from './interfaces'
import {
  ambar,
  azul,
  fontFamily,
  neutro,
  radius,
  semantic,
  verde
} from './tokens'

export const correttoThemeOptions: ThemeOptions = {
  palette: {
    primary: {
      main: semantic.primary,
      light: semantic.primaryLight,
      dark: semantic.primaryHover,
      contrastText: semantic.onPrimary
    },
    secondary: {
      main: semantic.secondary,
      light: verde[300],
      dark: semantic.secondaryHover,
      contrastText: semantic.onSecondary
    },
    success: {
      main: semantic.success,
      light: verde[500],
      dark: verde[800],
      contrastText: semantic.onPrimary
    },
    error: { main: semantic.error, dark: semantic.errorDark },
    warning: {
      main: ambar[700],
      light: ambar[300],
      dark: ambar[800],
      contrastText: semantic.onPrimary
    },
    info: {
      main: azul[500],
      light: azul[300],
      dark: azul[700],
      contrastText: semantic.onPrimary
    },
    // `grey.*` (sx e componentes do MUI) passa a ser a escala neutra.
    grey: {
      ...neutro,
      A100: neutro[100],
      A200: neutro[200],
      A400: neutro[400],
      A700: neutro[700]
    },
    text: {
      primary: semantic.text,
      secondary: semantic.textSecondary,
      disabled: semantic.textDisabled
    },
    background: {
      default: semantic.background,
      paper: semantic.surface
    },
    divider: semantic.border
  },
  shape: { borderRadius: radius.md },
  typography: {
    fontFamily,
    h1: { fontWeight: 800 },
    h2: { fontWeight: 800 },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 700 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: { fontWeight: 700, textTransform: 'none' }
  },
  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: radius.md },
        outlinedPrimary: { borderColor: azul[200] },
        // D2: o verde da marca so serve de fundo. Como texto, verde-escuro.
        outlinedSecondary: { color: verde[700], borderColor: verde[300] },
        textSecondary: { color: verde[700] }
      }
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600, borderRadius: radius.sm }
      }
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: semantic.border }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: `1px solid ${semantic.border}`,
          boxShadow: 'none'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: semantic.borderStrong
          },
          '&:hover:not(.Mui-disabled) .MuiOutlinedInput-notchedOutline': {
            borderColor: azul[400]
          }
        }
      }
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: verde[500], height: 3 }
      }
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          '&.Mui-selected': { color: semantic.primary }
        }
      }
    },
    // So a borda: o Drawer serve a menus e a filtros laterais. A sidebar azul
    // e da Fase 2, no proprio layout.
    MuiDrawer: {
      styleOverrides: {
        paper: { borderColor: semantic.border }
      }
    },
    MuiAppBar: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        colorPrimary: { backgroundColor: semantic.primary }
      }
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: neutro[50],
          '& .MuiTableCell-head': {
            fontWeight: 700,
            color: semantic.text
          }
        }
      }
    }
  }
}

export const correttoTheme = createTheme(correttoThemeOptions)

export default function Theme(props: ThemeInterface) {
  return <ThemeProvider theme={correttoTheme}>{props.children}</ThemeProvider>
}
